export interface LiveRoom {
  id: number;
  uuid: string;
  bj_nickname: string;
  title: string;
  description?: string;
  thumbnail?: string;
  avatar?: string;
  category?: string;
  tags?: string[];
  entry_level?: string;
  status: string;
  viewer_count: number;
  viewer_count_min?: number | null;
  viewer_count_max?: number | null;
  stream_url?: string;
  is_featured?: boolean;
}

export interface ChatRoom {
  id: number;
  title: string;
  host_nickname: string;
  host_image?: string;
  thumbnail?: string;
  avatar?: string;
  coins: number;
  access_level: number;
  entry_level?: string;
  max_capacity: number;
  current_occupancy: number;
  occupancy_label?: string;
  status: string;
  is_displayed?: boolean;
  can_enter?: boolean;
  opened_at?: string;
}

export interface ChatMessage {
  id: number;
  room_id: number;
  nickname: string;
  message: string;
  message_type?: 'text' | 'image' | 'gift' | string;
  image_url?: string | null;
  gift_amount?: number | null;
  sent_at?: string;
}

export const ONELIVE_OPEN_CHANNELS = 'onelive:open-channels';
export const ONELIVE_SEARCH_CHANGED = 'onelive:search-changed';

export const openChannelPanel = () => {
  window.dispatchEvent(new CustomEvent(ONELIVE_OPEN_CHANNELS));
};

export interface ExchangeItem {
  id: number;
  masked_username: string;
  amount: number;
  status?: string;
  status_label?: string;
  content?: string;
  bank_name?: string;
  account_number?: string;
  account_holder?: string;
  created_at?: string;
}

const getApi = () => (window as any).G7Core?.api;

export function hasAuthToken(): boolean {
  const api = getApi();
  if (!api?.getToken) {
    return false;
  }

  return Boolean(api.getToken());
}

export interface SiteFooterInfo {
  company_name: string;
  representative: string;
  address: string;
  business_number: string;
  commerce_number: string;
  privacy_officer: string;
  admin_email: string;
  copyright_year: string;
  legal_name: string;
  certificate_image: string;
}

export interface SiteBranding {
  site_name: string;
  logo_pc: string;
  logo_mobile: string;
  footer?: SiteFooterInfo;
  consultation?: SiteConsultationConfig;
  ui_messages?: {
    loginRequired?: string;
    enterBlocked?: string;
    broadcastPending?: string;
  };
}

export interface SiteConsultationConfig {
  channel_plugin_key: string;
  tidio_script: string;
  min_member_level: number;
  native_enabled?: boolean;
  agent_name?: string;
  agent_image?: string;
  ring_duration_sec?: number;
}

/** 将后台返回的绝对 URL 转为当前站点可用的相对路径 */
export function normalizeAssetUrl(url?: string | null): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('/')) return trimmed;
  try {
    const parsed = new URL(trimmed, window.location.origin);
    if (parsed.pathname.startsWith('/')) {
      return `${parsed.pathname}${parsed.search}`;
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

const LOCAL_ASSET_BASE = '/assets/templates/onelive-basic/ftv';

/** 外链主题/旧站图片地址转本地静态资源（仅装饰性小图标） */
export function resolveLocalThemeAsset(url?: string | null): string {
  const normalized = normalizeAssetUrl(url);
  if (!normalized) return '';

  if (normalized.includes('/theme/chating/assets/img/')) {
    const filename = normalized.split('/').pop()?.split('?')[0] ?? '';
    if (filename === 'item-s.png') return `${LOCAL_ASSET_BASE}/item-s.png`;
    if (filename === 'shop.png') return `${LOCAL_ASSET_BASE}/gift_shop_on.svg`;
    if (filename === 'check.png') return `${LOCAL_ASSET_BASE}/ico_support.svg`;
    if (filename === 'item.png') return `${LOCAL_ASSET_BASE}/mian-item.png`;
    if (filename === 'item1.png') return `${LOCAL_ASSET_BASE}/ico-money.png`;
    if (filename === 'item2.png') return `${LOCAL_ASSET_BASE}/gift_shop_on.svg`;
  }

  // 注意：/data/chat/item/* 是 아이템샵 大图（套餐图），必须保留原路径

  if (/^https?:\/\//i.test(url ?? '') && !normalized.startsWith('/api/')) {
    return '';
  }

  return normalized;
}

/** 商品图：保留本站 /data/chat/item/ 路径，仅把旧站绝对 URL 转为相对路径 */
export function resolveItemShopImageUrl(url?: string | null): string {
  const normalized = normalizeAssetUrl(url);
  if (!normalized) return '';

  const themeLocal = resolveLocalThemeAsset(url);
  if (themeLocal) return themeLocal;

  return normalized;
}

const ONELIVE_LOGO = '/assets/templates/onelive-basic/ftv/logo.svg';

const ONELIVE_FOOTER_CERT = '/assets/templates/onelive-basic/ftv/footer_img.png';

/** 底部 ISMS 认证图：忽略占位 svg，始终回退原站 png */
export function resolveFooterCertUrl(url?: string | null): string {
  const normalized = normalizeAssetUrl(url);
  if (!normalized || normalized.includes('footer_cert.svg')) {
    return ONELIVE_FOOTER_CERT;
  }
  return normalized;
}

/** Logo：优先用设置值，ngrok 绝对地址转相对路径，缺失 png 时回退 svg */
export function resolveLogoUrl(url?: string | null): string {
  const normalized = normalizeAssetUrl(url);
  if (!normalized) return ONELIVE_LOGO;
  if (normalized.startsWith('/img/site-logo')) return ONELIVE_LOGO;
  return normalized;
}

export interface FaqItem {
  id: number;
  category: string;
  question: string;
  answer: string;
  sort_order: number;
}

export interface FaqPageData {
  master: {
    id: number;
    title: string;
    head_html?: string | null;
    tail_html?: string | null;
    mobile_head_html?: string | null;
    mobile_tail_html?: string | null;
    header_image_url?: string | null;
    footer_image_url?: string | null;
  } | null;
  categories: string[];
  items: FaqItem[];
}

export async function fetchSiteBranding(): Promise<SiteBranding | null> {
  const api = getApi();
  if (!api) return null;
  try {
    const res = await api.get('/modules/onelive-admin/onelive/branding');
    const payload = res?.data ?? res;
    return payload?.data ?? payload ?? null;
  } catch {
    return null;
  }
}

export async function fetchFaqData(params?: { search?: string; masterId?: number }): Promise<FaqPageData> {
  const api = getApi();
  const fallback: FaqPageData = {
    master: null,
    categories: ['회원정보', '방송/시청', '후원/결제', '기타'],
    items: [],
  };
  if (!api) return fallback;
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.masterId) query.set('master_id', String(params.masterId));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const res = await api.get(`/modules/onelive-admin/onelive/faq${suffix}`);
    const payload = res?.data ?? res;
    return payload?.data ?? payload ?? fallback;
  } catch {
    return fallback;
  }
}

export async function fetchLiveRooms(
  limit = 200,
  search?: string,
  entryLevel?: string,
): Promise<LiveRoom[]> {
  const api = getApi();
  if (!api) return [];
  try {
    const params = new URLSearchParams({ per_page: String(limit) });
    if (search && search.length >= 2) {
      params.set('search', search);
    }
    if (entryLevel) {
      params.set('entry_level', entryLevel);
    }
    const res = await api.get(`/modules/onelive-live/live/rooms?${params.toString()}`);
    const payload = res?.data ?? res;
    const items = payload?.data ?? payload;
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

export async function fetchChatRooms(
  limit = 10,
  search?: string,
  entryLevel?: string,
): Promise<ChatRoom[]> {
  const api = getApi();
  if (!api) return [];
  try {
    const params = new URLSearchParams({ per_page: String(limit) });
    if (search && search.length >= 2) {
      params.set('search', search);
    }
    if (entryLevel) {
      params.set('entry_level', entryLevel);
    }
    const res = await api.get(`/modules/onelive-chat/chat/rooms?${params.toString()}`);
    return parseApiList<ChatRoom>(res);
  } catch {
    return [];
  }
}

export async function fetchProceedingChatRooms(limit = 10): Promise<ChatRoom[]> {
  const api = getApi();
  if (!api) return [];
  try {
    const params = new URLSearchParams({ per_page: String(limit) });
    const res = await api.get(`/modules/onelive-chat/chat/rooms/proceeding?${params.toString()}`);
    return parseApiList<ChatRoom>(res);
  } catch {
    return [];
  }
}

export async function fetchChatRoom(id: number | string): Promise<ChatRoom | null> {
  const api = getApi();
  if (!api || !id) return null;
  try {
    const res = await api.get(`/modules/onelive-chat/chat/rooms/${id}`);
    return parseChatPayload<ChatRoom>(res) ?? parseDirectRoom(res);
  } catch {
    return null;
  }
}

export async function enterChatRoom(id: number | string): Promise<{ room: ChatRoom | null; error?: string }> {
  const api = getApi();
  if (!api || !id) return { room: null, error: 'API unavailable' };
  try {
    const res = await api.post(`/modules/onelive-chat/chat/rooms/${id}/enter`);
    const room = parseChatPayload<ChatRoom>(res) ?? parseDirectRoom(res);
    return { room };
  } catch (err: unknown) {
    const message = extractApiErrorMessage(err) || '채팅방이 종료되였습니다.';
    return { room: null, error: message };
  }
}

export async function leaveChatRoom(id: number | string): Promise<void> {
  const api = getApi();
  if (!api || !id) return;
  try {
    await api.post(`/modules/onelive-chat/chat/rooms/${id}/leave`);
  } catch {
    // 퇴장 실패해도 목록 이동은 허용
  }
}

export async function fetchChatMessages(roomId: number | string, limit = 200): Promise<ChatMessage[]> {
  const api = getApi();
  if (!api || !roomId) return [];
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    const res = await api.get(`/modules/onelive-chat/chat/rooms/${roomId}/messages?${params.toString()}`);
    return parseApiList<ChatMessage>(res);
  } catch {
    return [];
  }
}

export async function sendChatMessage(roomId: number | string, message: string): Promise<ChatMessage | null> {
  const api = getApi();
  if (!api || !roomId || !message.trim()) return null;
  try {
    const res = await api.post(`/modules/onelive-chat/chat/rooms/${roomId}/messages`, { message: message.trim() });
    return parseChatPayload<ChatMessage>(res);
  } catch {
    return null;
  }
}

export async function sendChatImage(roomId: number | string, file: File): Promise<ChatMessage | null> {
  const api = getApi();
  if (!api || !roomId || !file) return null;
  try {
    const body = new FormData();
    body.append('image', file);
    const res = await api.post(`/modules/onelive-chat/chat/rooms/${roomId}/messages/image`, body);
    return parseChatPayload<ChatMessage>(res);
  } catch {
    return null;
  }
}

function parseChatPayload<T>(res: unknown): T | null {
  return parseApiRecord<Record<string, unknown>>(res) as T | null;
}

function extractApiErrorMessage(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const anyErr = err as {
    response?: { data?: { message?: string; errors?: string | Record<string, string[]> } };
    message?: string;
  };
  const data = anyErr.response?.data;
  if (typeof data?.errors === 'string' && data.errors.trim()) return data.errors;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof anyErr.message === 'string' && anyErr.message.trim()) return anyErr.message;
  return null;
}

export async function fetchLiveRoom(uuid: string): Promise<LiveRoom | null> {
  const api = getApi();
  if (!api || !uuid) return null;
  try {
    const res = await api.get(`/modules/onelive-live/live/rooms/${uuid}`);
    return res?.data ?? res ?? null;
  } catch {
    return null;
  }
}

export interface ItemShopItem {
  id: number;
  name: string;
  coin: number;
  price: number;
  img: string;
  deduct_coins?: number;
  purchase_level?: number | null;
  payment_guide?: string;
  description?: string;
  payment_guide_coin?: string;
  description_coin?: string;
  supports_coin_payment?: boolean;
  level_up?: string;
  can_purchase?: boolean;
  purchase_block_reason?: string | null;
}

export function isVipServiceShopItem(item: Pick<ItemShopItem, 'name'>): boolean {
  return item.name.includes('퀵환전') || item.name.includes('전용서비스');
}

/** purchase_level = 구매후레벨 (구매 완료 후 달성 등급) */
export function resolveItemPurchaseBlock(
  item: ItemShopItem,
  memberLevel = 2,
): string | null {
  if (item.can_purchase === false && item.purchase_block_reason) {
    return item.purchase_block_reason;
  }

  if (isVipServiceShopItem(item)) {
    return memberLevel <= 2 ? '죄송합니다. 다이아 회원만 이용가능합니다.' : null;
  }

  const targetLevel = item.purchase_level ?? 0;
  if (targetLevel > 0 && memberLevel >= targetLevel) {
    return '이미 해당 등급 이상입니다. 구매할 수 없습니다.';
  }

  return null;
}

export interface ItemShopOrder {
  id?: number;
  item_id: number;
  item_name: string;
  price: number;
  coin: number;
  ordered_at: string;
  status?: string;
  status_label?: string;
  depositor_name?: string;
  payment_method?: string;
}

export interface ItemShopCharge {
  amount: number;
  charged_at: string;
  note?: string;
  status?: string;
  status_label?: string;
}

export interface ItemShopPurchaseResult {
  order_id: number;
  item_name?: string;
  amount: number;
  coins?: number;
  status: string;
  message?: string;
}

export interface ItemShopWallet {
  display_name?: string;
  coin_balance: number;
  charged_amount: number;
  orders: ItemShopOrder[];
  charges?: ItemShopCharge[];
  ledger?: ItemShopLedgerEntry[];
}

export interface ItemShopLedgerEntry {
  id: number;
  content: string;
  amount: number;
  processed_at?: string;
  chat_order_id?: number | null;
  source?: string;
}

export const ONELIVE_SHOP_ORDER_ITEM_KEY = 'onelive:shop-order-item';

function isItemShopItem(value: unknown): value is ItemShopItem {
  if (!value || typeof value !== 'object') return false;
  const row = value as ItemShopItem;
  return Number.isFinite(row.id) && row.id > 0 && typeof row.name === 'string';
}

export function cacheItemShopOrderItem(item: ItemShopItem): void {
  if (typeof window === 'undefined' || !isItemShopItem(item)) return;
  try {
    sessionStorage.setItem(ONELIVE_SHOP_ORDER_ITEM_KEY, JSON.stringify(item));
  } catch {
    // ignore storage failures
  }
}

export function readCachedItemShopOrderItem(itemId?: number): ItemShopItem | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ONELIVE_SHOP_ORDER_ITEM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ItemShopItem;
    if (!isItemShopItem(parsed)) return null;
    if (itemId != null && itemId > 0 && parsed.id !== itemId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readCachedItemShopOrderItemForPage(itemId: number): ItemShopItem | null {
  if (itemId > 0) {
    const matched = readCachedItemShopOrderItem(itemId);
    if (matched) return matched;
  }
  return readCachedItemShopOrderItem();
}

async function waitForApi(maxAttempts = 20, delayMs = 100) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const api = getApi();
    if (api) return api;
    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
  }
  return null;
}

function parseItemShopItemResponse(res: unknown): ItemShopItem | null {
  const record = parseApiRecord<ItemShopItem>(res);
  if (isItemShopItem(record)) return record;

  if (!res || typeof res !== 'object') return null;
  const root = res as Record<string, unknown>;
  const payload = root.data ?? root;
  if (!payload || typeof payload !== 'object') return null;
  const body = payload as Record<string, unknown>;
  if (body.success === false) return null;

  const nested = body.data ?? body;
  if (isItemShopItem(nested)) return nested;
  if (nested && typeof nested === 'object' && isItemShopItem((nested as Record<string, unknown>).data)) {
    return (nested as Record<string, unknown>).data as ItemShopItem;
  }

  return null;
}

export async function fetchItemShopItems(): Promise<ItemShopItem[]> {
  const api = await waitForApi();
  if (!api) return [];
  try {
    const res = await api.get('/modules/onelive-shop/items');
    return parseApiList<ItemShopItem>(res).map((item) => ({
      ...item,
      img: resolveItemShopImageUrl(item.img),
    }));
  } catch {
    return [];
  }
}

function mergeItemShopItem(base: ItemShopItem | null, detail: ItemShopItem): ItemShopItem {
  if (!base) {
    return { ...detail, img: resolveItemShopImageUrl(detail.img) };
  }
  return {
    ...base,
    ...detail,
    img: resolveItemShopImageUrl(detail.img || base.img),
  };
}

export async function fetchItemShopItem(itemId: number): Promise<ItemShopItem | null> {
  if (!Number.isFinite(itemId) || itemId <= 0) return null;

  const cached = readCachedItemShopOrderItem(itemId);

  const api = await waitForApi(60, 150);
  if (!api) {
    return cached;
  }

  try {
    const res = await api.get(`/modules/onelive-shop/items/${itemId}`);
    const item = parseItemShopItemResponse(res?.data ?? res);
    if (item) return mergeItemShopItem(cached, item);
  } catch {
    // ignore and fall back to list
  }

  try {
    const res = await api.get('/modules/onelive-shop/items');
    const items = parseApiList<ItemShopItem>(res?.data ?? res);
    const fromList = items.find((row) => row.id === itemId);
    if (fromList) {
      return { ...fromList, img: resolveItemShopImageUrl(fromList.img) };
    }
  } catch {
    // ignore
  }

  return readCachedItemShopOrderItem(itemId);
}

/** 레이아웃 템플릿이 치환되지 않을 때 URL/G7 상태에서 주문 상품 ID 복원 */
export function resolveShopOrderItemId(itemId?: string | number): number {
  const raw = itemId != null ? String(itemId).trim() : '';
  if (raw && !raw.includes('{{') && !raw.includes('$route') && raw !== 'undefined') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  try {
    const state = (window as any).G7Core?.state?.get?.() || {};
    const global = state._global ?? state;
    const routeId = global?.route?.params?.id ?? global?.route?.id ?? state?.route?.params?.id;
    if (routeId != null) {
      const parsed = Number(routeId);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch {
    // ignore
  }

  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname.replace(/\/$/, '');
    const patterns = [
      /\/shop\/order\/(\d+)/,
      /\/order\/(\d+)/,
    ];
    for (const pattern of patterns) {
      const match = pathname.match(pattern);
      if (match?.[1]) {
        const parsed = Number(match[1]);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
      }
    }
  }

  return 0;
}

/** 코인 충전 금액 복원 */
export function resolveShopChargeAmount(chargeAmount?: string | number): number {
  const raw = chargeAmount != null ? String(chargeAmount).trim() : '';
  if (raw && !raw.includes('{{') && !raw.includes('$route') && raw !== 'undefined') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  try {
    const state = (window as any).G7Core?.state?.get?.() || {};
    const global = state._global ?? state;
    const queryAmount = global?.query?.amount ?? state?.query?.amount;
    if (queryAmount != null) {
      const parsed = Number(queryAmount);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch {
    // ignore
  }

  if (typeof window !== 'undefined') {
    const query = new URLSearchParams(window.location.search).get('amount');
    if (query) {
      const parsed = Number(query);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }

  return 0;
}

export async function fetchItemShopWallet(): Promise<ItemShopWallet | null> {
  if (!hasAuthToken()) {
    return null;
  }

  await resolveSessionUser();

  const api = await waitForApi();
  if (!api) return null;

  try {
    const res = await api.get('/modules/onelive-shop/wallet');
    const payload = res?.data ?? res;
    return payload?.data ?? payload ?? null;
  } catch {
    return null;
  }
}

function parseItemShopActionResult(res: unknown): ItemShopPurchaseResult {
  const root = (res && typeof res === 'object') ? res as Record<string, unknown> : {};
  const payload = (root.data && typeof root.data === 'object') ? root.data as Record<string, unknown> : root;
  if (payload.success === false) {
    throw new Error(String(payload.message || '처리 실패'));
  }
  const data = (payload.data && typeof payload.data === 'object')
    ? payload.data as Record<string, unknown>
    : payload;
  return {
    order_id: Number(data.order_id ?? 0),
    item_name: data.item_name != null ? String(data.item_name) : undefined,
    amount: Number(data.amount ?? 0),
    coins: data.coins != null ? Number(data.coins) : undefined,
    status: String(data.status ?? 'pending'),
    message: payload.message != null ? String(payload.message) : undefined,
  };
}

export async function itemShopPurchase(
  itemId: number,
  depositorName: string,
  paymentMethod: 'bank' | 'coin' = 'bank',
): Promise<ItemShopPurchaseResult> {
  const api = getApi();
  if (!api) throw new Error('api');
  try {
    const res = await api.post('/modules/onelive-shop/purchase', {
      item_id: itemId,
      depositor_name: depositorName.trim(),
      payment_method: paymentMethod,
    });
    return parseItemShopActionResult(res?.data ?? res);
  } catch (err: unknown) {
    const apiErr = err as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(apiErr?.response?.data?.message || apiErr?.message || '구매 실패');
  }
}

export async function itemShopCharge(
  amount: number,
  depositorName: string,
): Promise<ItemShopPurchaseResult> {
  const api = getApi();
  if (!api) throw new Error('api');
  try {
    const res = await api.post('/modules/onelive-shop/charge', {
      amount,
      depositor_name: depositorName.trim(),
    });
    return parseItemShopActionResult(res?.data ?? res);
  } catch (err: unknown) {
    const apiErr = err as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(apiErr?.response?.data?.message || apiErr?.message || '충전 실패');
  }
}

export const ONELIVE_REGISTER_CODE_KEY = 'onelive_register_code';
export const ONELIVE_REGISTER_CODE_OK_KEY = 'onelive_register_code_ok';

export function clearRegisterSession(): void {
  try {
    sessionStorage.removeItem(ONELIVE_REGISTER_CODE_KEY);
    sessionStorage.removeItem(ONELIVE_REGISTER_CODE_OK_KEY);
  } catch {
    // ignore
  }
}

export function saveRegisterCode(code: string): void {
  try {
    sessionStorage.setItem(ONELIVE_REGISTER_CODE_KEY, code);
    sessionStorage.setItem(ONELIVE_REGISTER_CODE_OK_KEY, '1');
  } catch {
    // ignore
  }
}

export function isRegisterCodeValidated(): boolean {
  try {
    return sessionStorage.getItem(ONELIVE_REGISTER_CODE_OK_KEY) === '1';
  } catch {
    return false;
  }
}

export function getSavedRegisterCode(): string {
  try {
    return sessionStorage.getItem(ONELIVE_REGISTER_CODE_KEY) || '';
  } catch {
    return '';
  }
}

export async function checkUsernameDuplicate(username: string): Promise<{ available: boolean; message: string }> {
  const api = getApi();
  if (!api) throw new Error('api');
  const res = await api.post('/modules/onelive-admin/member/check-username', { username });
  const payload = res?.data ?? res;
  const data = payload?.data ?? payload;
  return {
    available: Boolean(data?.available),
    message: String(data?.message || (data?.available ? '사용 가능한 아이디입니다.' : '이미 사용 중인 아이디입니다.')),
  };
}

export async function checkNicknameDuplicate(nickname: string): Promise<{ available: boolean; message: string }> {
  const api = getApi();
  if (!api) throw new Error('api');
  const res = await api.post('/modules/onelive-admin/member/check-nickname', { nickname });
  const payload = res?.data ?? res;
  const data = payload?.data ?? payload;
  return {
    available: Boolean(data?.available),
    message: String(data?.message || (data?.available ? '사용 가능한 닉네임입니다.' : '이미 사용 중인 닉네임입니다.')),
  };
}

export async function fetchOneliveEnvConfig(): Promise<Record<string, unknown> | null> {
  const api = getApi();
  if (!api) return null;
  try {
    const res = await api.get('/modules/onelive-admin/env-config');
    const payload = res?.data ?? res;
    return payload?.data ?? payload ?? null;
  } catch {
    return null;
  }
}

export async function checkRecommendCode(code: string): Promise<boolean> {
  const api = getApi();
  if (!api) return false;
  const res = await api.post('/modules/onelive-admin/recommend/validate', { code });
  const payload = res?.data ?? res;
  const data = payload?.data ?? payload;
  return Boolean(data?.valid);
}

export async function registerAccount(body: Record<string, unknown>): Promise<void> {
  const api = getApi();
  if (!api) throw new Error('api');
  try {
    const res = await api.post('/auth/register', body);
    const payload = res?.data ?? res;
    if (payload?.success === false) {
      throw new Error(payload?.message || '회원가입 실패');
    }
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { message?: string; errors?: Record<string, string[]> } };
      message?: string;
    };
    const errors = err.response?.data?.errors;
    const first = errors ? Object.values(errors)[0]?.[0] : null;
    throw new Error(first || err.response?.data?.message || err.message || '회원가입 실패');
  }
}

export async function loginAccount(email: string, password: string): Promise<void> {
  const core = (window as any).G7Core;
  if (core?.dispatch) {
    const result = await core.dispatch({
      handler: 'login',
      target: 'user',
      params: { body: { email, password } },
    });
    if (result?.success === false) {
      const message = result?.error?.message || '로그인 실패';
      throw new Error(message);
    }
    return;
  }
  const api = getApi();
  if (!api) throw new Error('api');
  try {
    const res = await api.post('/auth/login', { email, password });
    const payload = res?.data ?? res;
    if (payload?.success === false) {
      throw new Error(payload?.message || '로그인 실패');
    }
    const token = payload?.data?.token ?? payload?.token;
    if (token && api.setToken) api.setToken(token);
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    throw new Error(err.response?.data?.message || err.message || '로그인 실패');
  }
}

export async function logoutAccount(): Promise<void> {
  const api = getApi();
  if (api) {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    api.clearToken?.();
    api.removeToken?.();
  }

  clearCurrentUser();
  navigate('/');
}

export async function fetchExchanges(limit = 20): Promise<ExchangeItem[]> {
  const api = getApi();
  if (!api) return [];
  try {
    const res = await api.get(`/modules/onelive-money/money/exchanges?limit=${limit}`);
    const payload = res?.data ?? res;
    const items = payload?.data ?? payload;
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

export type ExchangeConfig = {
  id: number;
  name: string;
  is_active: boolean;
  settings: {
    error_message?: string;
    success_message?: string;
    min_amount?: number;
    max_amount?: number;
    min_amount_message?: string;
    max_amount_message?: string;
    use_hp_required?: boolean;
    use_hp_visible?: boolean;
    use_name_required?: boolean;
    use_name_visible?: boolean;
    content_head?: string;
    content_tail?: string;
  };
};

export type ExchangeSubmitPayload = {
  amount: number;
  bank_name: string;
  bank_number: string;
  account_name: string;
  phone: string;
  content: string;
};

export type ExchangeSubmitResult = {
  message?: string;
  data?: {
    id?: number;
    amount?: number;
    status?: string;
    created_at?: string;
  };
};

export async function submitExchangeRequest(
  payload: ExchangeSubmitPayload,
): Promise<ExchangeSubmitResult> {
  const api = getApi();
  if (!api) throw new Error('api');
  try {
    const res = await api.post('/modules/onelive-money/money/exchanges', payload);
    const body = (res?.data ?? res) as ExchangeSubmitResult & { success?: boolean; message?: string };
    if (body?.success === false) {
      throw new Error(body.message || '환전 신청 처리 중 오류가 발생했습니다.');
    }
    return body;
  } catch (err: unknown) {
    const apiErr = err as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(
      apiErr?.response?.data?.message || apiErr?.message || '환전 신청 처리 중 오류가 발생했습니다.',
    );
  }
}

export async function fetchExchangeConfig(): Promise<ExchangeConfig | null> {
  const api = getApi();
  if (!api) return null;
  try {
    const res = await api.get('/modules/onelive-money/money/configs/exchange');
    const payload = res?.data ?? res;
    return (payload?.data ?? payload) as ExchangeConfig;
  } catch {
    return null;
  }
}

export type OneliveSessionUser = {
  uuid?: string;
  name?: string;
  email?: string;
  nickname?: string;
  avatar?: string;
  mobile?: string;
  phone?: string;
  real_name?: string;
  gender?: string;
  gender_label?: string;
  member_level?: number;
  member_level_label?: string;
  is_admin?: boolean;
  is_super?: boolean;
  member_profile?: {
    real_name?: string;
    gender?: string;
    gender_label?: string;
    avatar_url?: string;
  };
};

export function parseApiRecord<T extends Record<string, unknown>>(res: unknown): T | null {
  if (!res || typeof res !== 'object') return null;
  const root = res as Record<string, unknown>;
  const payload = root.data ?? root;
  if (!payload || typeof payload !== 'object') return null;
  const nested = (payload as Record<string, unknown>).data ?? payload;
  if (!nested || typeof nested !== 'object') return null;
  if (Array.isArray(nested)) return null;
  return nested as T;
}

export function parseApiList<T>(res: unknown): T[] {
  const body = unwrapApiBody(res);
  if (Array.isArray(body)) return body as T[];
  if (!body || typeof body !== 'object') return [];
  const record = body as Record<string, unknown>;
  if (Array.isArray(record.data)) return record.data as T[];
  return [];
}

function unwrapApiBody(res: unknown): unknown {
  if (!res || typeof res !== 'object') return null;
  const root = res as Record<string, unknown>;
  const first = root.data ?? root;
  if (!first || typeof first !== 'object') return first;
  const record = first as Record<string, unknown>;
  if ('success' in record && 'data' in record) {
    return record.data;
  }
  return first;
}

function parseDirectRoom(res: unknown): ChatRoom | null {
  const body = unwrapApiBody(res);
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const record = body as Record<string, unknown>;
  if (typeof record.id === 'number' && typeof record.title === 'string') {
    return record as unknown as ChatRoom;
  }
  return null;
}

function isPlaceholderRealName(value?: string | null): boolean {
  const trimmed = value?.trim();
  return !trimmed || trimmed === '-';
}

export function resolveRealName(data: OneliveSessionUser | null | undefined): string {
  const candidates = [data?.member_profile?.real_name, data?.real_name];
  for (const candidate of candidates) {
    if (!isPlaceholderRealName(candidate)) {
      return candidate!.trim();
    }
  }
  return '';
}

export function syncCurrentUser(user: OneliveSessionUser | null | undefined): void {
  if (!user?.uuid) return;
  (window as { G7Core?: { state?: { set?: (payload: Record<string, unknown>, opts: { target: string }) => void } } }).G7Core?.state?.set?.(
    { currentUser: user },
    { target: 'global' },
  );
}

export function clearCurrentUser(): void {
  (window as { G7Core?: { state?: { set?: (payload: Record<string, unknown>, opts: { target: string }) => void } } }).G7Core?.state?.set?.(
    { currentUser: null },
    { target: 'global' },
  );

  try {
    const g7Config = (window as { G7Config?: { user?: OneliveSessionUser | null } }).G7Config;
    if (g7Config) g7Config.user = null;
  } catch {
    // ignore
  }
}

const SESSION_WAIT_MS = 3000;
const SESSION_POLL_MS = 50;

/** Wait for layout/API session hydration before login guards run. */
export async function resolveSessionUser(): Promise<OneliveSessionUser | null> {
  const immediate = getCurrentUser();
  if (immediate?.uuid) return immediate;

  if (!hasAuthToken()) {
    return null;
  }

  const profile = await fetchCurrentMemberProfile();
  if (profile?.uuid) return profile;

  return new Promise((resolve) => {
    const started = Date.now();

    const tick = () => {
      const user = getCurrentUser();
      if (user?.uuid) {
        resolve(user);
        return;
      }

      if (Date.now() - started >= SESSION_WAIT_MS) {
        resolve(null);
        return;
      }

      window.setTimeout(tick, SESSION_POLL_MS);
    };

    tick();
  });
}

export function getCurrentUser(): OneliveSessionUser | null {
  try {
    const state = (window as any).G7Core?.state?.get?.() || {};
    const user = state?._global?.currentUser ?? state?.currentUser;
    if (user?.uuid) return user;

    const g7Config = (window as any).G7Config;
    if (g7Config?.user?.uuid) return g7Config.user;

    return null;
  } catch {
    return null;
  }
}

export function resolveDisplayNickname(user: OneliveSessionUser | null, fallbackName?: string | null): string {
  const nickname = user?.nickname?.trim();
  const name = user?.name?.trim();
  const fallback = fallbackName?.trim();
  const email = user?.email?.trim();

  return nickname || name || fallback || email || '';
}

export async function fetchCurrentMemberProfile(): Promise<OneliveSessionUser | null> {
  const cached = getCurrentUser();
  if (!hasAuthToken()) {
    return cached;
  }

  const api = getApi();
  if (!api) return cached;
  try {
    const res = await api.get('/me');
    const data = parseApiRecord<OneliveSessionUser>(res);
    if (!data?.uuid) return cached;
    syncCurrentUser(data);
    return data;
  } catch {
    return cached;
  }
}

export const ONELIVE_MYPAGE_RESET = 'onelive:mypage-reset';

export const navigate = (path: string) => {
  const core = (window as any).G7Core;
  if (core?.dispatch) {
    core.dispatch({ handler: 'navigate', params: { path } });
    return;
  }
  window.location.assign(path);
};

/** 进入 마이페이지首页（关闭 코인내역 弹窗等子状态） */
export function navigateToMypageHub(): void {
  window.dispatchEvent(new CustomEvent(ONELIVE_MYPAGE_RESET));
  navigate('/mypage/profile');
}

export { buildMypageProfilePath } from './mypageMenu';

export function showOneliveToast(
  message: string,
  type: 'success' | 'error' | 'warning' | 'info' = 'info',
) {
  const core = (window as any).G7Core;
  if (core?.toast?.show) {
    core.toast.show(message, type);
    return;
  }
  core?.dispatch?.({ handler: 'toast', params: { type, message } });
}

const ONELIVE_ALERT_ID = 'onelive-alert-overlay';
const ONELIVE_ALERT_CHECK = '/assets/templates/onelive-basic/ftv/ico_support.svg';

/** 居中弹窗提示（参考站 pop-message / MessageDiv） */
export function showOneliveAlert(message: string, onClose?: () => void): void {
  if (typeof document === 'undefined') return;

  document.getElementById(ONELIVE_ALERT_ID)?.remove();

  const overlay = document.createElement('div');
  overlay.id = ONELIVE_ALERT_ID;
  overlay.className = 'onelive-pop-message';
  overlay.innerHTML = `
    <div class="onelive-pop-message__panel" role="alertdialog" aria-modal="true" aria-labelledby="onelive-pop-message-title">
      <div class="onelive-pop-message__body">
        <p class="onelive-pop-message__text" id="onelive-pop-message-title"></p>
      </div>
      <div class="onelive-pop-message__footer">
        <button type="button" class="onelive-pop-message__confirm btn border-red">
          <span><img src="${ONELIVE_ALERT_CHECK}" alt=""></span>확인
        </button>
      </div>
    </div>
  `;

  const textEl = overlay.querySelector('.onelive-pop-message__text');
  if (textEl) textEl.textContent = message;

  const close = () => {
    overlay.remove();
    document.removeEventListener('keydown', onKeyDown);
    onClose?.();
  };

  const onKeyDown = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') close();
  };

  overlay.querySelector('.onelive-pop-message__confirm')?.addEventListener('click', close);
  overlay.addEventListener('click', (ev) => {
    if (ev.target === overlay) close();
  });
  document.addEventListener('keydown', onKeyDown);

  document.body.appendChild(overlay);
  overlay.querySelector<HTMLButtonElement>('.onelive-pop-message__confirm')?.focus();
}

export function resolveChatRoomIdFromLocation(roomId?: string | number): string {
  const raw = roomId != null ? String(roomId).trim() : '';
  if (raw && !raw.includes('{{') && !raw.includes('$route') && raw !== 'undefined') {
    return raw;
  }

  try {
    const state = (window as { G7Core?: { state?: { get?: () => Record<string, unknown> } } }).G7Core?.state?.get?.() || {};
    const global = state._global as Record<string, unknown> | undefined;
    const route = (global?.route ?? state.route) as Record<string, unknown> | undefined;
    const query = (global?.query ?? state.query) as Record<string, unknown> | undefined;
    const fromQuery = query?.cr_id;
    if (fromQuery != null && String(fromQuery).trim()) {
      return String(fromQuery).trim();
    }
    const fromRoute = route?.id ?? state.id;
    if (fromRoute != null && String(fromRoute).trim()) {
      return String(fromRoute).trim();
    }
  } catch {
    // ignore
  }

  if (typeof window === 'undefined') return '';
  const fromUrlQuery = new URLSearchParams(window.location.search).get('cr_id')?.trim();
  if (fromUrlQuery) return fromUrlQuery;
  const match = window.location.pathname.match(/\/chat\/room\/([^/]+)/);
  return match?.[1] ?? '';
}

export function resolveNoticePostIdFromLocation(postId?: string | number): number {
  const raw = postId != null ? String(postId).trim() : '';
  if (raw && !raw.includes('{{') && !raw.includes('$route') && raw !== 'undefined') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  try {
    const state = (window as { G7Core?: { state?: { get?: () => Record<string, unknown> } } }).G7Core?.state?.get?.() || {};
    const global = state._global as Record<string, unknown> | undefined;
    const route = (global?.route ?? state.route) as Record<string, unknown> | undefined;
    const fromRoute = route?.id ?? state.id;
    if (fromRoute != null && String(fromRoute).trim()) {
      const parsed = Number(fromRoute);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch {
    // ignore
  }

  if (typeof window === 'undefined') return 0;
  const match = window.location.pathname.match(/\/board\/notice\/(\d+)/);
  if (match?.[1]) {
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }
  return 0;
}

export function getSearchQueryFromUrl(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('search')?.trim() ?? '';
}

export function buildLiveSearchPath(term: string): string {
  return `/?search=${encodeURIComponent(term.trim())}`;
}

export function navigateToLiveSearch(term: string): void {
  const q = term.trim();
  navigate(buildLiveSearchPath(q));
  window.dispatchEvent(new CustomEvent(ONELIVE_SEARCH_CHANGED, { detail: { search: q } }));
}

export const formatAmount = (n: number) => (n ?? 0).toLocaleString('ko-KR');

export interface NoticePost {
  id: number;
  title: string;
  row_type?: 'notice' | 'normal' | 'reply';
  is_notice?: boolean;
  is_new?: boolean;
  author?: { name?: string; avatar?: string };
  author_name?: string;
  created_at_formatted?: string;
}

export interface NoticePostDetail extends NoticePost {
  content?: string;
  content_mode?: string;
}

export interface NoticePostsData {
  board?: { name?: string };
  data: NoticePost[];
  pagination?: {
    current_page: number;
    last_page: number;
    total: number;
  };
}

export async function fetchNoticePosts(params?: {
  page?: number;
  search?: string;
}): Promise<NoticePostsData> {
  const api = getApi();
  const fallback: NoticePostsData = {
    data: [],
    pagination: { current_page: 1, last_page: 1, total: 0 },
  };
  if (!api) return fallback;
  try {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.search) query.set('search', params.search);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const res = await api.get(`/modules/sirsoft-board/boards/notice/posts${suffix}`);
    const envelope = res?.data !== undefined && res?.success !== undefined ? res : { data: res };
    const body = envelope?.data ?? envelope;
    const rows = Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.posts)
        ? body.posts
        : Array.isArray(body)
          ? body
          : [];
    return {
      board: body?.board,
      data: rows,
      pagination: body?.pagination ?? envelope?.pagination ?? fallback.pagination,
    };
  } catch {
    return fallback;
  }
}

export async function fetchNoticePost(id: number): Promise<NoticePostDetail | null> {
  const api = getApi();
  if (!api || !id) return null;
  try {
    const res = await api.get(`/modules/sirsoft-board/boards/notice/posts/${id}`);
    const payload = res?.data ?? res;
    const body = payload?.data ?? payload;
    return body?.post ?? body ?? null;
  } catch {
    return null;
  }
}
